import {isEqual} from 'lodash';
import {JobType} from '../../../../../api/types/aiReview.type';
import {
  AIReviewJobStatus,
  ICreateAIReviewJobPayload,
  ICreateIssueCodeJobPayload,
  ICreateRelevancyJobPayload,
  IUpdateAIReviewJobPayload,
} from '../../../../../api/types/aiReview.type';
import {AI_REVIEW_JOB_STATUS, AI_REVIEW_JOB_TYPE} from '../../constant/AiReviewConstants';
import {
  JobDetailFormData,
  ProtocolFormData,
} from '../../../../../redux/slice/aiReviewSliceV2';
import {isProtocolContentEdited} from './protocol-selection/protocolContentCompare';
import {pruneSparseValue, toSparseObject} from './sparseProtocolJson';

const toNullableId = (value?: number | null): number | null =>
  value == null ? null : value;

export type CreateJobProtocolFields = {
  reviewProtocolVersionID: number | null;
  parentProtocolVersionID: number | null;
  isProtocolModified: boolean;
  isManual: boolean;
};

/**
 * AVR-22264 create-job protocol association:
 * - Manual (no saved protocol): isManual true, version IDs null, include JSON
 * - Saved unmodified: isManual false, reviewProtocolVersionID set, omit JSON
 * - Saved modified: isManual false, reviewProtocolVersionID null,
 *   parentProtocolVersionID = original version, include JSON
 */
export const resolveCreateJobProtocolFields = (
  formData: JobDetailFormData,
  protocol?: ProtocolFormData,
  initialProtocol?: ProtocolFormData
): CreateJobProtocolFields => {
  if (formData.isManual) {
    return {
      reviewProtocolVersionID: null,
      parentProtocolVersionID: null,
      isProtocolModified: false,
      isManual: true,
    };
  }

  const isContentEdited =
    protocol != null && initialProtocol != null
      ? isProtocolContentEdited(protocol, initialProtocol)
      : false;
  const isProtocolModified =
    Boolean(formData.isProtocolModified) || isContentEdited;
  const protocolVersionId = toNullableId(protocol?.protocolVersionId);
  const reviewProtocolVersionID = toNullableId(
    formData.reviewProtocolVersionID
  );
  const parentProtocolVersionID = toNullableId(
    formData.parentProtocolVersionID
  );
  const appliedVersionId = isProtocolModified
    ? parentProtocolVersionID ?? protocolVersionId ?? reviewProtocolVersionID
    : protocolVersionId ?? reviewProtocolVersionID ?? parentProtocolVersionID;

  if (appliedVersionId == null) {
    return {
      reviewProtocolVersionID: null,
      parentProtocolVersionID: null,
      isProtocolModified: false,
      isManual: true,
    };
  }

  if (isProtocolModified) {
    return {
      reviewProtocolVersionID: null,
      parentProtocolVersionID: appliedVersionId,
      isProtocolModified: true,
      isManual: false,
    };
  }

  return {
    reviewProtocolVersionID: appliedVersionId,
    parentProtocolVersionID: null,
    isProtocolModified: false,
    isManual: false,
  };
};

const buildBasePayload = (
  formData: JobDetailFormData,
  jobType: JobType,
  status: AIReviewJobStatus,
  protocolFields: CreateJobProtocolFields
): Omit<
  ICreateAIReviewJobPayload,
  'caseContext' | 'issueCoding' | 'relevancy'
> => ({
  jobType,
  jobName: formData.jobDetail.jobName,
  description: formData.jobDetail.description ?? '',
  groupID: Number(formData.jobDetail.selectedGroup?.id) || null,
  groundTruthTagId: formData.groundTruthTagId ?? null,
  sourceType: formData.sourceSelectionMode,
  sourceID: formData.sourceSelection ?? 0,
  reviewProtocolVersionID: protocolFields.reviewProtocolVersionID,
  parentProtocolVersionID: protocolFields.parentProtocolVersionID,
  isProtocolModified: protocolFields.isProtocolModified,
  parentReviewJobID: formData.parentReviewJobID ?? null,
  isManual: protocolFields.isManual,
  status,
});

const shouldIncludeProtocolJson = (protocolFields: CreateJobProtocolFields): boolean =>
  protocolFields.isManual || protocolFields.isProtocolModified;

const emptyClearedValue = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return [];
  }
  if (typeof value === 'string') {
    return '';
  }
  if (value != null && typeof value === 'object') {
    return {};
  }
  return value;
};

/**
 * Field-level edit diff. Empty UI placeholders (`[{id:''}]`) match GET `[]`,
 * so unchanged sections stay `{}`. Cleared fields still send `[]` / `''`.
 */
export const diffProtocolJsonFields = <T extends object>(
  current: T,
  initial?: T
): Partial<T> => {
  const changed: Partial<T> = {};
  (Object.keys(current) as Array<keyof T>).forEach((key) => {
    const currentValue = current[key];
    const prunedCurrent = pruneSparseValue(currentValue);
    const prunedInitial = pruneSparseValue(initial?.[key]);
    if (isEqual(prunedCurrent, prunedInitial)) {
      return;
    }
    changed[key] = (
      prunedCurrent === undefined
        ? emptyClearedValue(currentValue)
        : prunedCurrent
    ) as T[keyof T];
  });
  return changed;
};

const resolveProtocolSections = (
  formData: JobDetailFormData,
  jobType: JobType,
  protocol?: ProtocolFormData,
  initialProtocol?: ProtocolFormData,
  mode: 'sparse' | 'diff' = 'diff'
):
  | Pick<ICreateIssueCodeJobPayload, 'caseContext' | 'issueCoding'>
  | Pick<ICreateRelevancyJobPayload, 'caseContext' | 'relevancy'> => {
  const currentCaseContext = protocol?.caseContext ?? formData.caseContext;
  const currentIssueCoding = protocol?.issueCoding ?? formData.issueCoding;
  const currentRelevance =
    protocol?.relevanceCriteria ?? formData.relevanceCriteria;

  const caseContext =
    mode === 'sparse'
      ? (toSparseObject(currentCaseContext) as Partial<typeof currentCaseContext>)
      : diffProtocolJsonFields(
          currentCaseContext,
          initialProtocol?.caseContext
        );

  if (jobType === AI_REVIEW_JOB_TYPE.issueCoding) {
    return {
      caseContext,
      issueCoding:
        mode === 'sparse'
          ? (toSparseObject(
              currentIssueCoding
            ) as Partial<typeof currentIssueCoding>)
          : diffProtocolJsonFields(
              currentIssueCoding,
              initialProtocol?.issueCoding
            ),
    };
  }

  return {
    caseContext,
    relevancy:
      mode === 'sparse'
        ? (toSparseObject(currentRelevance) as Partial<typeof currentRelevance>)
        : diffProtocolJsonFields(
            currentRelevance,
            initialProtocol?.relevanceCriteria
          ),
  };
};

export const buildCreateJobPayload = (
  formData: JobDetailFormData,
  jobType: JobType,
  status: AIReviewJobStatus = AI_REVIEW_JOB_STATUS.draft,
  protocol?: ProtocolFormData,
  initialProtocol?: ProtocolFormData
): ICreateAIReviewJobPayload => {
  const protocolFields = resolveCreateJobProtocolFields(
    formData,
    protocol,
    initialProtocol
  );
  const base = buildBasePayload(formData, jobType, status, protocolFields);

  if (!shouldIncludeProtocolJson(protocolFields)) {
    return base;
  }

  // Create/submit: full sparse protocol JSON (all filled fields), not field diffs.
  const sections = resolveProtocolSections(
    formData,
    jobType,
    protocol,
    initialProtocol,
    'sparse'
  );

  if (jobType === AI_REVIEW_JOB_TYPE.issueCoding) {
    const payload: ICreateIssueCodeJobPayload = {
      ...base,
      jobType: 'ISSUECODE',
      ...(sections as Pick<
        ICreateIssueCodeJobPayload,
        'caseContext' | 'issueCoding'
      >),
    };
    return payload;
  }

  const payload: ICreateRelevancyJobPayload = {
    ...base,
    jobType: 'RELEVANCY',
    ...(sections as Pick<
      ICreateRelevancyJobPayload,
      'caseContext' | 'relevancy'
    >),
  };
  return payload;
};

export const buildUpdateJobPayload = (
  formData: JobDetailFormData,
  jobType: JobType,
  status: AIReviewJobStatus = AI_REVIEW_JOB_STATUS.draft,
  protocol?: ProtocolFormData,
  initialProtocol?: ProtocolFormData
): IUpdateAIReviewJobPayload => {
  const protocolFields = resolveCreateJobProtocolFields(
    formData,
    protocol,
    initialProtocol
  );
  const base = {
    ...buildBasePayload(formData, jobType, status, protocolFields),
    jobId: Number(formData.jobId),
  };

  if (!shouldIncludeProtocolJson(protocolFields)) {
    return base;
  }

  const sections = resolveProtocolSections(
    formData,
    jobType,
    protocol,
    initialProtocol,
    protocolFields.isManual || protocolFields.isProtocolModified
      ? 'sparse'
      : 'diff'
  );

  if (jobType === AI_REVIEW_JOB_TYPE.issueCoding) {
    const payload: ICreateIssueCodeJobPayload & {jobId: number} = {
      ...base,
      jobType: 'ISSUECODE',
      ...(sections as Pick<
        ICreateIssueCodeJobPayload,
        'caseContext' | 'issueCoding'
      >),
    };
    return payload;
  }

  const payload: ICreateRelevancyJobPayload & {jobId: number} = {
    ...base,
    jobType: 'RELEVANCY',
    ...(sections as Pick<
      ICreateRelevancyJobPayload,
      'caseContext' | 'relevancy'
    >),
  };
  return payload;
};

